import { useState } from 'react';
import { Box, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  SBase,
  SFormikTextfield,
  SFormikCheckbox,
  SText
} from '@setvi/shared/components';
import { PenIconV2, TagWithHeartIcon } from '@setvi/shared/components/sicons';
import { useStyles } from './styled';

export const PREDEFINED_LABELS = [
  'option',
  'accessory',
  'good',
  'better',
  'best',
  'custom'
];

interface EditOptionDetailsDialogProps {
  itemId: string;
  itemName?: string;
  note?: string;
  customLabel?: string;
  labelType?: string;
  includeInTotalCost?: boolean;
  onClose: () => void;
  onSave: (
    itemId: string,
    data: {
      note?: string;
      labelType: string;
      customLabel?: string;
      includeInTotalCost?: boolean;
    }
  ) => void;
}

const validationSchema = Yup.object({
  note: Yup.string(),
  customLabel: Yup.string().max(11, 'Max 11 characters'),
  includeInTotalCost: Yup.boolean()
});

type TabType = 'note' | 'label';

export const EditOptionDetailsDialog = ({
  itemId,
  itemName,
  note = '',
  customLabel = '',
  labelType = PREDEFINED_LABELS[0],
  includeInTotalCost = false,
  onClose,
  onSave
}: EditOptionDetailsDialogProps) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState<TabType>('note');
  const [selectedLabel, setSelectedLabel] = useState(
    PREDEFINED_LABELS.includes(labelType) ? labelType : 'Custom'
  );

  return (
    <Formik
      initialValues={{
        note,
        labelType,
        customLabel,
        includeInTotalCost
      }}
      validationSchema={validationSchema}
      onSubmit={values => {
        onSave(itemId, {
          note: values.note,
          labelType: selectedLabel,
          customLabel: selectedLabel === 'custom' ? values?.customLabel : '',
          includeInTotalCost: values.includeInTotalCost
        });
        onClose();
      }}>
      {formik => (
        <Form>
          <SBase
            style={{
              background: '#fff'
            }}
            container={{
              style: {
                overflow: 'hidden'
              }
            }}
            disablePadding
            header={{ title: 'Edit Option Details', onClose }}
            footer={{
              onConfirm: () => formik.handleSubmit(),
              onClose,
              confirmLabel: 'Save Changes',
              closeLabel: 'Cancel',
              disableConfirm:
                selectedLabel === 'custom' && !formik.values.customLabel?.trim()
            }}
            minHeight={400}
            width="75vw"
            height="60vh">
            <Box className={classes.mainContent}>
              {/* Sidebar */}
              <Box className={classes.sidebar}>
                <Box
                  className={`${classes.sidebarItem} ${activeTab === 'note' ? classes.sidebarItemActive : ''}`}
                  onClick={() => setActiveTab('note')}>
                  <PenIconV2 className={classes.sidebarItemIcon} />
                  <SText className={classes.sidebarItemText}>Edit Note</SText>
                </Box>
                <Box
                  className={`${classes.sidebarItem} ${activeTab === 'label' ? classes.sidebarItemActive : ''}`}
                  onClick={() => setActiveTab('label')}>
                  <TagWithHeartIcon className={classes.sidebarItemIcon} />
                  <SText className={classes.sidebarItemText}>
                    Edit Option Label
                  </SText>
                </Box>
              </Box>

              {/* Content Area */}
              <Box className={classes.contentArea}>
                {activeTab === 'note' && (
                  <Box className={classes.contentSection}>
                    <SText
                      size="lg"
                      weight="semibold"
                      className={classes.sectionHeading}>
                      Edit Note
                    </SText>
                    <SFormikTextfield
                      name="note"
                      label="Note"
                      placeholder={
                        itemName
                          ? `For ${itemName}, add your note here...`
                          : 'Add your note here...'
                      }
                      multiline
                      minRows={4}
                      maxRows={8}
                    />
                  </Box>
                )}

                {activeTab === 'label' && (
                  <>
                    <Box className={classes.contentSection}>
                      <SText
                        size="lg"
                        weight="semibold"
                        className={classes.sectionHeading}>
                        Edit Option Label
                      </SText>
                      <SText size="sm" className={classes.sectionDescription}>
                        Choose a predefined label or create a custom one for
                        this item.
                      </SText>
                      <Box className={classes.labelsContainer}>
                        {PREDEFINED_LABELS.map(label => (
                          <Box
                            key={label}
                            className={`${classes.labelPill} ${selectedLabel === label ? classes.labelPillSelected : ''}`}
                            onClick={() => {
                              setSelectedLabel(label);
                              if (label !== PREDEFINED_LABELS[5]) {
                                formik.setFieldValue('customLabel', '');
                              }
                              formik.setFieldValue(
                                'includeInTotalCost',
                                label === PREDEFINED_LABELS[1]
                              );
                            }}>
                            {label.charAt(0).toUpperCase() + label.slice(1)}
                          </Box>
                        ))}
                      </Box>

                      {/* Custom Label Input */}
                      {selectedLabel === 'custom' && (
                        <Box className={classes.customLabelSection}>
                          <SFormikTextfield
                            name="customLabel"
                            label="Custom Label"
                            placeholder="Enter Label"
                            maxLength={11}
                            disableErrorMessage
                            InputProps={{
                              style: { backgroundColor: '#FFFFFF', height: 44 }
                            }}
                            endIcon={
                              formik.values.customLabel ? (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    formik.setFieldValue('customLabel', '')
                                  }>
                                  <CloseIcon
                                    style={{
                                      fontSize: 16,
                                      color: '#5D7083',
                                      marginRight: -12
                                    }}
                                  />
                                </IconButton>
                              ) : undefined
                            }
                          />
                          <SText
                            size="xs"
                            title="(max 11 characters)"
                            fontColor="#333D47"
                            style={{
                              marginLeft: 2
                            }}
                          />
                        </Box>
                      )}
                    </Box>

                    <Box className={classes.checkboxSection}>
                      <SFormikCheckbox
                        name="includeInTotalCost"
                        label="Include in total cost"
                        labelClass={classes.labelClass}
                      />
                      <SText size="sm" color="secondary" noLineClamp>
                        If selected, this item's price will be added to the
                        total quote amount. If you leave it unchecked, the item
                        will appear in the quote but will not affect the
                        calculated totals.
                      </SText>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </SBase>
        </Form>
      )}
    </Formik>
  );
};
